import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

type DeleteStopperRuleResponse = {
  code: string;
  message: string;
  processId: UUID;
};

/**
 * Deletes the stopper rule for an invoice process with the given stopperRuleId.
 * @param stopperRuleId - The ID of the invoice stopper rule to delete.
 * @returns A promise that resolves when the stopper rule is deleted.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/delete_invoice_process_stopper_rule_vadmin_invoice_stopper_rule__stopper_rule_id__delete
 */
export const deleteInvoiceProcessStopperRule = async (stopperRuleId: UUID) => {
  try {
    const response = await vestigasApi
      .delete(ENDPOINT.INVOICE_REPROCESSING.DELETE_STOPPER_RULE(stopperRuleId))
      .json<DeleteStopperRuleResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error deleting invoice process stopper rule',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(($) => $.deleteStopperRule.error404, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.deleteStopperRule.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.deleteStopperRule.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
